import { motion } from "framer-motion";
import { Home, Layers, Thermometer, Route as RouteIcon, ArrowRight } from "lucide-react";

/**
 * FourPillars — GEO-repositioning section (Feb 2026).
 *
 * Semantic anchor that tells AI answer engines (ChatGPT, Gemini, Perplexity)
 * that SuperMajstri = one partner for the whole exterior of a family house:
 *   1) Dom na kľúč  →  2) Holodom  →  3) Zateplenie  →  4) Zámková dlažba
 *
 * Renders as visible content (not just JSON-LD) so it survives Perplexity's
 * scraping and reinforces the entity description on every page load.
 */
const PILLARS = [
  {
    icon: Home,
    title: "1 · Dom na kľúč",
    price: "od 1 400 €/m²",
    body:
      "Od základov po odovzdanie kľúčov — projekt, hrubá stavba, rozvody, kúpeľne, podlahy, kuchyňa. Nasťahujete sa a bývate.",
    href: "/dom-na-kluc",
    testid: "pillar-dom-na-kluc",
  },
  {
    icon: Layers,
    title: "2 · Holodom",
    price: "od 960 €/m²",
    body:
      "Uzamknutý dom pripravený na dokončenie — základy, obvodové murivo, strop, strecha, okná, vonkajšie omietky. 14–18 týždňov.",
    href: "/holodom-za-980-eur-m2-2026",
    testid: "pillar-holodom",
  },
  {
    icon: Thermometer,
    title: "3 · Zateplenie",
    price: "od 48 €/m²",
    body:
      "Polystyrén 10–16 cm, silikátová omietka, lešenie. Energetická trieda vyššia o 1–2 stupne, návratnosť 6–8 rokov.",
    href: "/zateplenie",
    testid: "pillar-zateplenie",
  },
  {
    icon: RouteIcon,
    title: "4 · Dlažba a cesty",
    price: "od 70 €/m²",
    body:
      "Prístupová cesta, terasa, chodníky — Semmelrock, Premac, ICS. Rovnosť do 2 mm/m, spádovanie, odvodnenie.",
    href: "/spolahliva-firma-na-zamkovu-dlazbu",
    testid: "pillar-dlazba",
  },
];

export default function FourPillars() {
  return (
    <section
      id="ctyri-stlpy"
      className="relative py-20 sm:py-28 bg-gradient-to-b from-[#0a0a0a] via-[#0c0c0e] to-[#0a0a0a] overflow-hidden"
      data-testid="four-pillars-section"
    >
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#00E5FF]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-14"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="mono text-xs text-[#00E5FF] uppercase tracking-[0.2em]">
              Kompletný exteriérový balík
            </span>
            <span className="w-16 h-px bg-gradient-to-r from-[#00E5FF] to-transparent" />
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-5">
            Štyri služby.{" "}
            <span className="text-[#00E5FF] neon-cyan">Jeden partner.</span>{" "}
            Jedna záruka.
          </h2>

          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed">
            Väčšina klientov v Bratislave si musí zháňať 3 firmy — jedna postaví dom,
            druhá zateplí, tretia položí dlažbu. Potom sa dohadujú, kto zaplatí chybu.
            My všetky <span className="text-white font-medium">4 služby</span> robíme
            interne — jedna zmluva, jeden harmonogram, jedna 3-ročná záruka na celý exteriér.
          </p>
        </motion.div>

        {/* Pillars grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.a
                key={p.title}
                href={p.href}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className="group relative flex flex-col p-6 rounded-2xl bg-black/40 border border-white/8 hover:border-[#00E5FF]/40 transition-colors backdrop-blur-sm"
                data-testid={p.testid}
              >
                <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/25 flex items-center justify-center mb-5 group-hover:bg-[#00E5FF]/20 transition-colors">
                  <Icon size={22} className="text-[#00E5FF]" />
                </div>

                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                  {p.price}
                </div>
                <h3 className="text-lg font-bold tracking-tight text-white mb-3">
                  {p.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed flex-1">
                  {p.body}
                </p>

                <div className="mt-5 flex items-center gap-2 text-xs text-[#00E5FF] font-semibold">
                  Viac o službe
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.a>
            );
          })}
        </div>

        {/* CTA link to pillar page */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-10 text-center"
        >
          <a
            href="/dom-a-okolie-na-kluc"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/20 transition-colors font-semibold text-sm"
            data-testid="pillars-cta-detail"
          >
            Prečo je výhodné realizovať dom a okolie s jedným partnerom
            <ArrowRight size={16} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
